// NPM
import React, { useState, useEffect, useRef } from "react";
import _ from 'lodash';

// State
import { useSelector } from 'react-redux';

// Services
import apiService from "services/apiService";
import dataService from './services/dataService';

// Components
import SeatDistributionArea from './SeatDistributionArea';
import SeatDistributionLegend from './SeatDistributionLegend';

// Constants
import constants from './constants';

export default function SeatDistributionGraph() {
  // Hooks
  useEffect(() => loadData(), []);

  // State
  const [seatDistributionData, setSeatDistributionData] = useState({});
  const campaignConfig = useSelector(state => state.campaignConfig.value);
  const responsiveView = useSelector(state => state.responsiveView.value);
  const region = useSelector(state => state.region.value);
  const parties = useSelector(state => state.parties.value);
  const partyIds = parties.map(p => p.id);

  const { seatsPerRow } = _.get(campaignConfig, "sections.SeatDistribution", {});

  const loadData = async () => {
    const response = await fetch(apiService.apiURL("seat_distribution"));
    const jsonData = await response.json();
    setSeatDistributionData(jsonData);
  }

  // Short-circuit
  if(_.isBlank(seatDistributionData) || _.isBlank(region) || _.isBlank(parties)) {
    return null;
  }

  const formattedData = dataService.formatSeatDistributionData(seatDistributionData, seatsPerRow);
  const sectionData = _.map(formattedData, area => {
    return area.map((group, groupIndex) => ({
      index: groupIndex + 1,
      rows: group.length
    }))
  });

  // Collect stats on the section
  //   How many rows it has
  //   How many groups it has
  // The get the longest one, this will be used to determine the height of the SVG
  // The SVG only needs to be tall enough to render the tallest section
  const longestSection = _
    .chain(sectionData)
    .map(group => {
      return {
        rowCount: _.sumBy(group, "rows"),
        numGroups: group.length
      }
    })
    .orderBy("rowCount", ["desc"])
    .first()
    .value();

  let viewBoxWidth = 0;
  viewBoxWidth += constants.seatLength * seatsPerRow * 2;
  viewBoxWidth += seatsPerRow * constants.seatGap * 2;
  viewBoxWidth += constants.middleGap;

  let viewBoxHeight = 0;
  viewBoxHeight += longestSection.rowCount * constants.seatLength;
  viewBoxHeight += longestSection.rowCount * constants.seatGap;
  viewBoxHeight += longestSection.numGroups * constants.groupMarginTop;

  let svgWidth;
  switch(responsiveView) {
    case "teeny":
      svgWidth = 300;
      break;
      
    case "mobile":
      svgWidth = 350;
      break;

    case "tablet":
      svgWidth = 600;
      break;

    case "desktop":
      svgWidth = 800;
      break;

    default:
      svgWidth = 400;
      break;
  }

  return (
    <div className="Signal__section-visualization SeatDistributionGraph">
      <svg width={ svgWidth } viewBox={ `0 0 ${ viewBoxWidth } ${ viewBoxHeight }` }>
        {/* Give the SVG some padding */}
        <g transform="translate(1,1)">
          <g transform="translate(0,0)">
            <SeatDistributionArea data={ formattedData.leftData } />
          </g>

          <g transform={ `translate(${ (viewBoxWidth / 2) + (constants.middleGap / 2) }, 0)` }>
            <SeatDistributionArea data={ formattedData.rightData } />
          </g>
        </g>
      </svg>

      <SeatDistributionLegend formattedData={ formattedData } />
    </div>
  )
}